import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, IndianRupee, Receipt } from "lucide-react";
import { useParams } from "react-router-dom";
import MacJitLogo from "../components/MacJitLogo";
import api from "../lib/api";
import { toast } from "sonner";

function formatMoney(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function LedgerPaymentPage() {
  const { id } = useParams();
  const [bill, setBill] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get(`/public/ledger/bills/${id}`);
    setBill(res.data || {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) return resolve(true);
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const pay = async () => {
    setBusy(true);
    try {
      const orderRes = await api.post(`/public/ledger/bills/${id}/route/order`);
      const order = orderRes.data || {};
      if (order.paid) {
        await load();
        toast.success("Payment already completed");
        return;
      }
      if (order.payment_link && order.provider !== "razorpay") {
        window.location.href = order.payment_link;
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay. Check your internet connection.");
      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: order.currency || "INR",
          name: order.name || "Terminal Mini",
          description: order.description || "Mini bill",
          order_id: order.order_id,
          prefill: order.prefill || {},
          theme: { color: "#f97316" },
          modal: { ondismiss: async () => {
            await api.post(`/public/ledger/bills/${id}/payment-failed`, { reason: "Payment cancelled" }).catch(() => {});
            await load().catch(() => {});
            reject(new Error("Payment cancelled"));
          } },
          handler: async (resp) => {
            try {
              const res = await api.post(`/public/ledger/bills/${id}/route/verify`, {
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_signature: resp.razorpay_signature,
              });
              setBill(res.data || {});
              toast.success("Payment successful. Invoice email sent if email is available.");
              resolve();
            } catch (err) {
              toast.error(err.response?.data?.detail || "Payment verification failed");
              reject(err);
            }
          },
        });
        rzp.on("payment.failed", (resp) => {
          toast.error(resp.error?.description || "Payment failed");
          api.post(`/public/ledger/bills/${id}/payment-failed`, { reason: resp.error?.description || "Payment failed" }).then((res) => setBill(res.data || {})).catch(() => {});
          reject(new Error("Payment failed"));
        });
        rzp.open();
      });
    } catch (err) {
      if (err.message !== "Payment cancelled" && err.message !== "Payment failed") {
        toast.error(err.response?.data?.detail || err.message || "Could not start payment");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <main className="mx-auto grid min-h-screen max-w-2xl place-items-center px-6 py-12">
        <section className="w-full border border-zinc-800 bg-zinc-900/40 p-6 sm:p-10">
          <div className="flex items-center gap-3">
            <MacJitLogo size={38} />
            <div>
              <p className="font-display text-xl font-black tracking-tighter">MINI <span className="text-orange-500">/ BILL</span></p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Secure payment receipt</p>
            </div>
          </div>
          <div className="mt-10">
            {bill?.paid ? <CheckCircle2 className="h-12 w-12 text-emerald-400" /> : <Receipt className="h-12 w-12 text-orange-500" />}
            <h1 className="mt-5 font-display text-4xl font-black uppercase tracking-tighter">{bill?.title || "Mini bill"}</h1>
            <p className="mt-3 font-display text-6xl font-black text-orange-500">₹{formatMoney(bill?.total)}</p>
            <div className="mt-5 space-y-2 border border-zinc-800 bg-zinc-950/50 p-4 text-sm">
              <div className="flex justify-between gap-4 text-zinc-400">
                <span>Subtotal</span>
                <span className="font-mono text-zinc-200">₹{formatMoney(bill?.subtotal || bill?.total)}</span>
              </div>
              {(bill?.billing_addons || []).map((line, index) => (
                <div key={`${line.key || line.label || "addon"}-${index}`} className="flex justify-between gap-4 text-zinc-400">
                  <span>{line.label || "Tax / charge"}</span>
                  <span className="font-mono text-zinc-200">₹{formatMoney(line.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between gap-4 border-t border-zinc-800 pt-3 font-display font-black text-zinc-100">
                <span>Total payable</span>
                <span>₹{formatMoney(bill?.total)}</span>
              </div>
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
              {bill?.customer_name || "Customer"} · {bill?.paid ? "Paid" : "Payment pending"}
            </p>
            {!bill?.paid && (
              <button onClick={pay} disabled={busy} className="mt-8 flex w-full items-center justify-center gap-2 bg-orange-500 py-4 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
                <IndianRupee className="h-4 w-4" /> {busy ? "Processing..." : `Pay ₹${formatMoney(bill?.total)} now`}
              </button>
            )}
            {bill?.paid && <p className="mt-8 border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">Payment complete. Your invoice has been sent if an email was provided.</p>}
          </div>
        </section>
      </main>
    </div>
  );
}
